import { ChangeDetectionStrategy, Component, computed, inject, Signal, viewChild } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { take } from 'rxjs';
import { AppRoutes, DrillsRoutes, NAVIGATION_TOKEN, NavigationProvider, TranslationService } from '@core';
import {
  SzAppShellComponent,
  SzButtonComponent,
  SzCtaBarComponent,
  SzIconComponent,
  SzPageTitleComponent,
  SzSuccessOverlayService,
  SzTopBarComponent
} from '@ui';
import {
  CreateDrillCommandHandler,
  CreateDrillCommandPayload,
  CreateDrillCommandResult
} from '../../application/handlers';
import { DrillDraftStorage } from '../../application/state/drill-draft.storage';
import { DrillFormComponent } from '../drill-form/drill-form.component';
import { DrillFormValue } from '../drill-form/drill-form-value';

@Component({
  selector: 'lib-drill-create',
  templateUrl: './drill-create.component.html',
  styleUrls: ['./drill-create.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    TranslateModule,
    SzAppShellComponent,
    SzTopBarComponent,
    SzPageTitleComponent,
    SzCtaBarComponent,
    SzButtonComponent,
    SzIconComponent,
    DrillFormComponent
  ],
  providers: [CreateDrillCommandHandler]
})
export class DrillCreateComponent {
  private readonly form: Signal<DrillFormComponent | undefined> = viewChild(DrillFormComponent);

  private readonly navigation: NavigationProvider = inject(NAVIGATION_TOKEN);
  private readonly createHandler: CreateDrillCommandHandler = inject(CreateDrillCommandHandler);
  private readonly translation: TranslationService = inject(TranslationService);
  private readonly successOverlay: SzSuccessOverlayService = inject(SzSuccessOverlayService);
  private readonly drillDraftStorage: DrillDraftStorage = inject(DrillDraftStorage);

  readonly initialValue: CreateDrillCommandPayload | null = this.drillDraftStorage.takeCopySeed();

  private readonly returnToPlan: boolean = this.drillDraftStorage.takeReturnToPlanRequested();

  readonly canSubmit: Signal<boolean> = computed((): boolean => this.form()?.canSubmit() ?? false);

  onSubmit(value: DrillFormValue): void {
    this.successOverlay
      .showDuring$<CreateDrillCommandResult>(this.createHandler.execute(value), {
        loadingTitle: this.translation.translate('drills.create.saving'),
        successTitle: this.translation.translate('drills.create.success')
      })
      .pipe(take(1))
      .subscribe((r: CreateDrillCommandResult) => {
        if (this.returnToPlan) {
          this.drillDraftStorage.setLastCreated(r.drill);
          this.navigation.back();
          return;
        }
        this.navigation.replace(['/', AppRoutes.DRILLS, DrillsRoutes.DETAIL, r.drillId]);
      });
  }

  triggerSubmit(): void {
    this.form()?.submit();
  }
}
